import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, MapPin, Phone, Mail, Globe, Wrench, ExternalLink, ShieldCheck, FileText } from "lucide-react";
import { api, formatApiError, type PublicPortal } from "@/lib/api";

const STATUS_TONE: Record<string, string> = {
  scheduled: "bg-blue-100 text-blue-700", confirmed: "bg-emerald-100 text-emerald-700",
  completed: "bg-slate-100 text-slate-600", cancelled: "bg-red-100 text-red-600", no_show: "bg-amber-100 text-amber-700",
};

export default function CustomerPortal() {
  const { token = "" } = useParams();
  const [data, setData] = useState<PublicPortal | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.publicPortalGet(token).then(setData).catch((e) => setError(formatApiError(e)));
  }, [token]);

  if (error) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6" data-testid="portal-error">
      <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <ShieldCheck className="mx-auto mb-3 h-8 w-8 text-slate-300" />
        <p className="text-slate-700">{error}</p>
        <p className="mt-2 text-xs text-slate-400">Please contact the business for a new link.</p>
      </div>
    </div>
  );
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-400">Loading your portal…</div>;

  const b = data.business;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-portal">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-6 py-6">
          <p className="text-xs font-medium uppercase tracking-wide text-blue-600">Customer Portal</p>
          <h1 className="mt-1 font-display text-2xl font-semibold text-slate-900" data-testid="portal-business-name">{b.name}</h1>
          <p className="text-sm text-slate-500">Welcome back, {data.customer_name}.</p>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-8 px-6 py-8">
        <section data-testid="portal-appointments">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><CalendarDays className="h-4 w-4 text-blue-600" /> Upcoming appointments</h2>
          {data.appointments.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-400">You have no upcoming appointments.</div>
          ) : (
            <div className="space-y-3">
              {data.appointments.map((a, i) => (
                <div key={i} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`portal-appt-${i}`}>
                  <div>
                    <p className="font-medium text-slate-900">{a.service || "Appointment"}</p>
                    <p className="text-sm text-slate-500">{a.date_display} · {a.time_display}</p>
                    <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[a.status] || "bg-slate-100 text-slate-600"}`}>{a.status.replace("_", " ")}</span>
                  </div>
                  {a.can_reschedule && a.manage_url && (
                    <a href={a.manage_url} className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100" data-testid={`portal-manage-${i}`}>
                      Reschedule / cancel <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="portal-estimates">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><FileText className="h-4 w-4 text-blue-600" /> Estimates & invoices</h2>
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
            {data.estimates.supported ? "Your estimates and invoices will appear here soon." : "Online estimates and invoices aren't enabled for this business yet."}
          </div>
        </section>

        {data.services.length > 0 && (
          <section data-testid="portal-services">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Wrench className="h-4 w-4 text-blue-600" /> Our services</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {data.services.map((s, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="font-medium text-slate-900">{s.name}</p>
                  {s.description && <p className="mt-1 text-sm text-slate-500">{s.description}</p>}
                  {s.price != null && <p className="mt-1 text-sm text-slate-600">${s.price}{s.pricing_type ? ` (${s.pricing_type})` : ""}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        <section data-testid="portal-contact">
          <h2 className="mb-3 text-sm font-semibold text-slate-800">Contact {b.name}</h2>
          <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
            {b.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {b.phone}</p>}
            {b.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {b.email}</p>}
            {b.website && <p className="flex items-center gap-2"><Globe className="h-4 w-4 text-slate-400" /> {b.website}</p>}
            {b.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {b.address}</p>}
            {!b.phone && !b.email && !b.website && !b.address && <p className="text-slate-400">Contact details coming soon.</p>}
          </div>
        </section>

        <p className="pb-6 text-center text-xs text-slate-400">This secure portal shows only your information.</p>
      </main>
    </div>
  );
}
